import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const BTN = "bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all duration-200 shadow-sm active:scale-[0.98]";
const INPUT = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

export default function Login() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [code, setCode] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [remember, setRemember] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const resend = async () => {
    if (resendIn > 0) return;
    setError("");
    try {
      await api.post("/auth/resend-otp", { otp_token: otpToken });
      setInfo("A new code has been emailed to you.");
      setResendIn(30);
      const t = setInterval(() => setResendIn((n) => { if (n <= 1) { clearInterval(t); return 0; } return n - 1; }), 1000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setOtpToken(data.otp_token);
        setCode("");
        setMode("otp");
        setInfo(`We emailed a 6-digit code to ${email}. Enter it below to finish signing in.`);
      } else {
        setSession(data);
        navigate("/portal");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { otp_token: otpToken, code, remember_device: remember });
      setSession(data);
      navigate("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setInfo(data.message || "If that email exists, a reset link has been sent.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = () => { setEmail("demo@vapropertycare.com"); setPassword("Demo@1234"); };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(600px circle at 30% 20%, rgba(5,150,105,0.08), transparent 60%)" }} />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md">
        <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-emerald-700 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
          <div className="flex items-center gap-2.5 mb-8">
            <img src="/va-logo-orange.png" alt="VA Property Care" className="h-10 w-10 object-contain" />
            <span className="font-display text-lg font-bold text-slate-900">VA Property Care</span>
          </div>

          {mode === "login" && (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Client Login</h1>
              <p className="text-sm text-slate-500 mt-1.5">Access your property portal.</p>
              <form onSubmit={handleLogin} className="mt-7 space-y-4">
                <div>
                  <label className="text-sm text-slate-600">Email</label>
                  <input required type="email" data-testid="login-email-input" value={email}
                    onChange={(e) => setEmail(e.target.value)} className={INPUT} />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Password</label>
                  <div className="relative">
                    <input required type={showPwd ? "text" : "password"} data-testid="login-password-input" value={password}
                      onChange={(e) => setPassword(e.target.value)} className={`${INPUT} pr-11`} />
                    <button type="button" onClick={() => setShowPwd((s) => !s)} data-testid="toggle-password-visibility"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 hover:text-slate-700">
                      {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {error && <p data-testid="login-error" className="text-sm text-rose-600">{error}</p>}
                <button type="submit" disabled={busy} data-testid="login-submit-button"
                  className={`${BTN} w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg disabled:opacity-60`}>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
                </button>
              </form>
              <div className="mt-5 flex items-center justify-between text-sm">
                <button data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                  className="text-emerald-700 hover:text-emerald-800 font-medium">Forgot password?</button>
                <button data-testid="fill-demo-button" onClick={fillDemo} className="text-slate-500 hover:text-slate-800">Use demo account</button>
              </div>
            </>
          )}

          {mode === "otp" && (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-emerald-600" /> Verify it's you
              </h1>
              <p className="text-sm text-slate-500 mt-1.5">Enter the 6-digit code we emailed you.</p>
              <form onSubmit={handleVerify} className="mt-7 space-y-4">
                <div>
                  <label className="text-sm text-slate-600">6-digit code</label>
                  <input required inputMode="numeric" pattern="[0-9]*" maxLength={6} data-testid="otp-code-input" value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className={`${INPUT} tracking-[0.5em] text-center text-lg font-mono`} placeholder="••••••" />
                </div>
                {info && <p data-testid="otp-info" className="text-sm text-emerald-700">{info}</p>}
                {error && <p data-testid="otp-error" className="text-sm text-rose-600">{error}</p>}
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-device-checkbox" className="h-4 w-4 accent-emerald-600" />
                  Remember this device for 30 days
                </label>
                <button type="submit" disabled={busy || code.length !== 6} data-testid="otp-submit-button"
                  className={`${BTN} w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg disabled:opacity-60`}>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & Sign In
                </button>
              </form>
              <button onClick={() => { setMode("login"); setError(""); setInfo(""); setCode(""); }}
                className="mt-5 text-sm text-emerald-700 hover:text-emerald-800 font-medium">← Start over</button>
              <button onClick={resend} disabled={resendIn > 0} data-testid="otp-resend-button"
                className="mt-5 ml-4 text-sm text-slate-500 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
                {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
              </button>
            </>
          )}

          {mode === "forgot" && (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Reset Password</h1>
              <p className="text-sm text-slate-500 mt-1.5">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleForgot} className="mt-7 space-y-4">
                <div>
                  <label className="text-sm text-slate-600">Email</label>
                  <input required type="email" data-testid="forgot-email-input" value={email}
                    onChange={(e) => setEmail(e.target.value)} className={INPUT} />
                </div>
                {error && <p className="text-sm text-rose-600">{error}</p>}
                {info && <p data-testid="forgot-info" className="text-sm text-emerald-700">{info}</p>}
                <button type="submit" disabled={busy} data-testid="forgot-submit-button"
                  className={`${BTN} w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg disabled:opacity-60`}>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send Reset Link
                </button>
              </form>
              <button onClick={() => { setMode("login"); setError(""); setInfo(""); }}
                className="mt-5 text-sm text-emerald-700 hover:text-emerald-800 font-medium">← Back to login</button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
